import { Box, Card, CardContent, CardMedia, Grid, Typography } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import React, { useContext, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { SectionServices } from './SectionServices';
import { Controller, useForm, useWatch } from 'react-hook-form';
import Loading from '../../../../components/Loader/Loading';
import { SectionContext } from './Sections';

const SectionReorder = () => {
    const { fetchSectionList, list, loading, setLoading } = useContext(SectionContext);
    const { control, reset, setValue } = useForm({
        mode: "onChange",
        defaultValues: {
            sections: []
        }
    });

    const [sections, setSections] = useState([]);
    const [originalOrder, setOriginalOrder] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const watchedSections = useWatch({
        control,
        name: "sections"
    });

    const autoSave = async (updatedSections) => {
        try {
            setLoading(true);
            const response = await SectionServices.reorderSections(updatedSections);

            if (response.success == '1') {
                const newOriginalOrder = [...sections];
                setOriginalOrder(newOriginalOrder);
            } else {
                handleCancel();
                Swal.fire({
                    icon: 'error',
                    title: 'Save Failed',
                    text: response.message || 'Failed to update section positions',
                    timer: 2000
                });
            }
        } catch (error) {
            handleCancel();
            Swal.fire({
                icon: 'error',
                title: 'Save Failed',
                text: 'An error occurred while updating section positions',
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const newList = [...sections];
        const draggedItem = newList[draggedIndex];

        newList.splice(draggedIndex, 1);
        newList.splice(dropIndex, 0, draggedItem);

        setSections(newList);

        const updatedSections = newList.map((item, index) => ({
            id: item.id,
            position: index + 1
        }));

        updatedSections.forEach((section, index) => {
            setValue(`sections[${index}].id`, section.id);
            setValue(`sections[${index}].position`, section.position);
        });

        await autoSave(updatedSections);

        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    useEffect(() => {
        if (list) {
            const activeList = list.filter(item => item.isActive);
            const sortedList = activeList.sort((a, b) => a.position - b.position);
            setSections(sortedList);
            setOriginalOrder([...sortedList]);

            const formData = {
                sections: sortedList.map((item, index) => ({
                    id: item.id,
                    position: index + 1
                }))
            };
            reset(formData);
        }
    }, [list]);

    const handleCancel = () => {
        setSections([...originalOrder]);

        const formData = {
            sections: originalOrder.map(item => ({
                id: item.id,
                position: item.position
            }))
        };
        reset(formData);
    };

    return (
        <Box component="div">
            {loading && <Loading />}
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1, color: '#3e3e3e' }}>
                Reorder Sections
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#666', mb: 1 }}>
                Drag and drop to reorder active sections
            </Typography>
            <Grid container spacing={1} sx={{ maxHeight: { xs: '300px', lg: 'calc(100vh - 250px)' }, overflow: 'auto' }}>
                {sections.length > 0 && sections.map((item, index) => (
                    <Grid
                        item
                        xs={12}
                        key={item.id}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        sx={{
                            opacity: draggedIndex === index ? 0.5 : 1,
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                            position: 'relative',
                            '&::before': dragOverIndex === index && draggedIndex !== index ? {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                border: '2px dashed #2196f3',
                                borderRadius: '4px',
                                zIndex: 1,
                                pointerEvents: 'none'
                            } : {}
                        }}
                    >
                        <Card sx={{
                            position: 'relative',
                            mb: 1,
                            '&:hover': {
                                boxShadow: 3,
                                transform: 'translateY(-2px)',
                                transition: 'all 0.2s ease-in-out'
                            },
                            backgroundImage: item.backgroundImage ? `url(${process.env.REACT_APP_IMAGE_STORE_PATH}/${process.env.REACT_APP_ENV_MODE=='development'?'staging':'live'}/customer-app/sections/${item.backgroundImage})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundColor: item.backgroundColor || '#fff',
                        }}>
                            
                            

                            

                            {/* {item.backgroundImage && (
                                <CardMedia
                                    component="img"
                                    alt={item.title}
                                    height="60"
                                    image={`${process.env.REACT_APP_IMAGE_STORE_PATH}/${item.backgroundImage}`}
                                    sx={{ objectFit: 'cover' }}
                                />
                            )} */}
                            {/* {item.backgroundColor && !item.backgroundImage && (
                                <Box sx={{ height: 20, backgroundColor: item.backgroundColor }} />
                            )} */}
                            <CardContent sx={{ p: '8px !important'}}>
                                <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                                    <Box sx={{
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: 28,
                                        height: 28,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.75rem',
                                        fontWeight: 'bold'
                                    }}>
                                    {watchedSections?.[index]?.position || index + 1}
                                    </Box>
                                    <Box sx={{bgcolor:'#fff',p:1,borderRadius:1,overflow:'hidden'}}>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }} noWrap>
                                            {item.title}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.6rem', color: '#666' }}>
                                            {item.sectionType?.replace(/_/g, ' ')}
                                        </Typography>
                                        <Controller
                                            control={control}
                                            name={`sections[${index}].position`}
                                            render={({ field }) => (
                                                <input {...field} type="hidden" />
                                            )}
                                        />
                                    </Box>
                                    <DragIndicator sx={{ color: '#666', fontSize: '18px', cursor: 'grab' }} />

                                </Box>
                                
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {sections.length === 0 && !loading && (
                    <Grid item xs={12}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', textAlign: 'center', py: 2 }}>
                            No active sections to reorder
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default SectionReorder;
